import { Button } from "@/components/ui/button";
import Text from "@/components/Text";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState, type Dispatch, type FC, type SetStateAction } from "react";
import { SquarePlus } from "lucide-react";
import type { FavoriteList as FavoriteListType } from "@/API";
import { Checkbox } from "@/components/ui/checkbox";
import { addRestaurantFavoriteList } from "@/store/userFavoriteListState";
import { useTranslation } from "react-i18next";

type Props = {
  onClose: () => void;
  isLoading: boolean;
  restaurantsId: string[];
  setCreateNewFavorite: Dispatch<SetStateAction<boolean>>;
  favoriteList: FavoriteListType[];
};

const FavoriteList: FC<Props> = ({
  onClose,
  isLoading,
  restaurantsId,
  setCreateNewFavorite,
  favoriteList,
}) => {
  const { t } = useTranslation();

  const [selectedFavoriteList, setSelectedFavoriteList] = useState<{
    id: string;
    name: string;
  } | null>(null);

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <Text fontSize={20} fontWeight={600}>
            {t("favorite_list_save_in")}
          </Text>
        </DialogTitle>
      </DialogHeader>

      <DialogDescription>
        <div className="flex flex-col gap-[8px]">
          {favoriteList.map((favorite) => (
            <div
              key={favorite.id}
              style={{ border: "1px solid #E9D8D8" }}
              className="rounded-[8px] p-[12px] gap-[12px] flex items-center"
            >
              <Checkbox
                checked={selectedFavoriteList?.id === favorite.id}
                onCheckedChange={() =>
                  setSelectedFavoriteList(
                    selectedFavoriteList?.id === favorite.id
                      ? null
                      : { id: favorite.id, name: favorite.name },
                  )
                }
              />
              <div className="flex flex-col">
                <Text fontSize={14} fontWeight={600}>
                  {favorite.name}
                </Text>
                <Text
                  fontSize={14}
                  fontWeight={400}
                >{`${favorite.restaurantCount} restaurant(s)`}</Text>
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center gap-[16px] mt-[16px]">
          <Text fontSize={14} fontWeight={400}>
            {t("or")}
          </Text>
          <Button
            variant="primaryOutlined"
            onClick={() => {
              setCreateNewFavorite(true);
            }}
          >
            <SquarePlus />
            {t("create_list")}
          </Button>
        </div>
      </DialogDescription>

      <DialogFooter className="w-[full] flex items-center mt-[32px]">
        <Button
          type="button"
          variant="secondaryFilled"
          onClick={() => onClose()}
          className="w-[50%]"
        >
          {t("cancel")}
        </Button>
        <Button
          className="w-[50%]"
          disabled={isLoading || !selectedFavoriteList}
          onClick={() => {
            addRestaurantFavoriteList({
              favoriteListName: selectedFavoriteList?.name || "",
              favoriteListId: selectedFavoriteList?.id || "",
              restaurantsId: restaurantsId,
            });
          }}
        >
          {t("save")}
        </Button>
      </DialogFooter>
    </>
  );
};

export default FavoriteList;
